import { default as Select } from 'antd/es/select';
import { default as Skeleton } from 'antd/es/skeleton';
import { useCallback, useEffect, useState } from 'react';

import { dispatchGAEvent } from 'utils/googleAnalytics';
import { logEventToHotjar } from 'utils/logging';

import {
  type CreatedPayoutDraftData,
  useGetDisbursementPaymentInstruments,
  useGetDisbursementProductSchemas,
  usePostDisbursementPayoutDrafts
} from 'api';

import { useCurrentOrganisation, useOrganisationId } from 'hooks/context';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';

type DraftSelection = {
  instrumentName?: string;
  corridorId?: number;
  schemaId?: number;
};

type CreateDraftProps = {
  setPayoutDraft: React.Dispatch<
    React.SetStateAction<CreatedPayoutDraftData | undefined>
  >;
};

export const CreateDraft = ({ setPayoutDraft }: CreateDraftProps) => {
  const organisation = useCurrentOrganisation();

  //
  // Post draft mutation
  //
  const { mutate: createDraft } = usePostDisbursementPayoutDrafts({
    mutation: {
      onSuccess: (data) => {
        setPayoutDraft(data.data);
      },
      onError: (_) => {}
    }
  });

  //
  // Selected state
  //
  const [draftSelection, updateDraftSelection] = useState<DraftSelection>({});

  //
  // Select options
  //
  const { paymentInstrumentOptions, isPending: isPaymentInstrumentsPending } =
    usePaymentInstrumentOptions();

  const { corridorOptions, setCorridors: setCorridorOptions } =
    useCorridorOptions(updateDraftSelection);

  const {
    schemaOptions,
    isPending: isSchemasPending,
    isError: isSchemasError
  } = useSchemaOptions(updateDraftSelection, draftSelection.corridorId);

  //
  // Submit handler
  //
  const onSubmit = useCallback(() => {
    // Validate required data
    if (!draftSelection.corridorId || !draftSelection.schemaId) {
      return;
    }

    createDraft({
      organisationId: organisation.id,
      data: {
        product_id: draftSelection.corridorId,
        schema_id: draftSelection.schemaId
      }
    });

    logEventToHotjar('new-manual-payout:proceeded');

    dispatchGAEvent('create_manual_payout_proceeded', {
      from: 'selectProduct',
      to: 'payoutRequest',
      version: 'v2'
    });
  }, [createDraft, organisation.id, draftSelection]);

  return (
    <div className="flex w-64 flex-col gap-4">
      {isPaymentInstrumentsPending ? (
        <>
          <Skeleton.Node active className="!h-12 !w-64" />
          <Skeleton.Node active className="!h-12 !w-64" />
        </>
      ) : (
        <>
          <Select<string, PaymentInstrumentOption>
            data-testid="create_draft.instrument"
            onChange={(instrumentName, instrument) => {
              updateDraftSelection(() => ({ instrumentName }));
              setCorridorOptions(
                (instrument as PaymentInstrumentOption).corridorOptions
              );
            }}
            value={draftSelection.instrumentName}
            options={paymentInstrumentOptions}
            placeholder="Select transfer type"
          />
          <Select
            data-testid="create_draft.corridor"
            onChange={(corridor) => {
              updateDraftSelection((current) => ({
                ...current,
                corridorId: corridor,
                schemaId: undefined
              }));
            }}
            value={draftSelection.corridorId}
            options={corridorOptions}
            placeholder="Select corridor"
            disabled={corridorOptions.length <= 1}
          />
        </>
      )}
      {isSchemasPending ? (
        <Skeleton.Node active className="!h-12 !w-64" />
      ) : (
        <Select
          data-testid="create_draft.schema"
          key={`corridor_schema_${draftSelection.corridorId}`}
          onChange={(value) => {
            updateDraftSelection((current) => ({
              ...current,
              schemaId: value
            }));
          }}
          value={draftSelection.schemaId}
          options={schemaOptions}
          placeholder="Select schema"
          disabled={
            schemaOptions === undefined ||
            schemaOptions.length <= 1 ||
            isSchemasError
          }
        />
      )}
      {isSchemasError && (
        <div className="text-xs text-red-500">
          Something went wrong. Please try another corridor or contact support.
        </div>
      )}

      <PrimaryButton
        data-testid="create_draft.create_button"
        disabled={!draftSelection.corridorId || !draftSelection.schemaId}
        onClick={onSubmit}
      >
        Create
      </PrimaryButton>
    </div>
  );
};

/**
 * Fetches Payment Instruments transformed into Select Options
 */
const usePaymentInstrumentOptions = () => {
  const organisation = useCurrentOrganisation();

  const { data: paymentInstrumentOptions = [], isPending } =
    useGetDisbursementPaymentInstruments(organisation.id, {
      query: {
        select(data) {
          return data.data.map((instrument) => ({
            value: instrument.local_instrument_family,
            label: instrument.local_instrument_family,
            instrument,
            corridorOptions: instrument.products.map((corridor) => ({
              value: corridor.id,
              label: corridor.corridor,
              corridor
            }))
          }));
        }
      }
    });

  return { paymentInstrumentOptions, isPending };
};

type PaymentInstrumentOption = ReturnType<
  typeof usePaymentInstrumentOptions
>['paymentInstrumentOptions'][number];

/**
 * State management for corridor options
 */
const useCorridorOptions = (
  updateDraftSelection: React.Dispatch<React.SetStateAction<DraftSelection>>
) => {
  const [corridorOptions, setCorridorOptions] = useState<
    PaymentInstrumentOption['corridorOptions']
  >([]);

  const setCorridors = useCallback(
    (options: PaymentInstrumentOption['corridorOptions']) => {
      setCorridorOptions(() => options);
      // Select first option if only one option is available
      updateDraftSelection((current) => ({
        ...current,
        corridorId: options?.length === 1 ? options[0].value : undefined,
        schemaId: undefined
      }));
    },
    [updateDraftSelection]
  );

  return {
    corridorOptions,
    setCorridors
  };
};

/**
 * Fetches Schemas transformed into Select Options based on selected Corridor
 */
const useSchemaOptions = (
  updateDraftSelection: React.Dispatch<React.SetStateAction<DraftSelection>>,
  corridorId?: number
) => {
  const organisationId = useOrganisationId<number>();

  const {
    data: schemaOptions,
    isFetching: isSchemasPending,
    isError: isSchemasError
  } = useGetDisbursementProductSchemas(
    organisationId,
    { product_id: corridorId as number },
    {
      query: {
        enabled: corridorId !== undefined,
        select(res) {
          return (
            res.data?.map((schema) => ({
              value: schema.id,
              label: schema.label
            })) ?? []
          );
        }
      }
    }
  );

  // This sadly needs to be in a useEffect as "useQuery -> select" runs on every rerender
  useEffect(() => {
    // Select first option if only one option is available
    if (schemaOptions?.length === 1) {
      updateDraftSelection((current) => ({
        ...current,
        schemaId: schemaOptions[0].value as unknown as number
      }));
    }
  }, [schemaOptions, updateDraftSelection]);

  return {
    schemaOptions,
    isPending: isSchemasPending,
    isError: isSchemasError
  };
};
